import React, { useState, useEffect } from 'react';
import { formatEuro, formatPercent, formatNumber } from '../../utils/currency';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Alert,
  Skeleton,
  Chip,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BusinessIcon from '@mui/icons-material/Business';
import PieChartIcon from '@mui/icons-material/PieChart';
import { getStocks, StockDto } from '../../services/stocksApi';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

interface PortfolioSummary {
  totalValue: number;
  totalCost: number;
  totalGainLoss: number;
  totalGainLossPercentage: number;
  stocksCount: number;
  etfsCount: number;
  dividendYield: number;
  monthlyDividends: number;
}

interface TopPerformer {
  symbol: string;
  name: string;
  gainLossPercentage: number;
  currentValue: number;
}

const StocksDashboard: React.FC = () => {
  
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | null>(null);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [returnsData, setReturnsData] = useState<Array<{ symbol: string; totalReturn: number; noDivReturn: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load stocks data
      const stocks = await getStocks();
      
      // Calculate portfolio summary
      const summary = calculatePortfolioSummary(stocks);
      setPortfolioSummary(summary);
      
      // Calculate top performers
      const performers = calculateTopPerformers(stocks);
      setTopPerformers(performers);

      // Compute returns: with and without dividends per symbol (EUR-consistent, account for sales and cambio)
      const returnsRows = stocks.map(s => {
        const purchases = Array.isArray(s.purchases) ? s.purchases : [];
        const sales = Array.isArray(s.sales) ? s.sales : [];
        // Determine effective cambio for current price; fallback to average purchase cambio if missing
        let stockCambio: number = typeof (s as any).currentCambio === 'number' ? Number((s as any).currentCambio) : 1;
        const currency = (s as any).currency;
        if (currency && currency !== 'EUR' && (!stockCambio || stockCambio === 0)) {
          const cambios: number[] = purchases
            .map((p: any) => (typeof p.cambio === 'number' ? Number(p.cambio) : (typeof p.pricePerShareEUR === 'number' && p.pricePerShare ? Number(p.pricePerShareEUR) / Number(p.pricePerShare) : NaN)))
            .filter((v: number) => Number.isFinite(v) && v > 0) as number[];
          if (cambios.length > 0) {
            stockCambio = cambios.reduce((a: number, b: number) => a + b, 0) / cambios.length;
          }
        }

        // Map sold quantity per purchase
        const soldByPurchase = new Map<string, number>();
        sales.forEach((sale: any) => {
          const spList = Array.isArray(sale.salePurchases) ? sale.salePurchases : [];
          spList.forEach((sp: any) => {
            const pid = sp.purchasePublicId;
            const qty = Number(sp.quantitySold || 0);
            if (pid) soldByPurchase.set(pid, (soldByPurchase.get(pid) || 0) + qty);
          });
        });

        // Remaining quantity and EUR cost basis
        let remainingQuantity = 0;
        let remainingCostBasisEUR = 0;
        purchases.forEach((p: any) => {
          const totalQty = Number(p.quantity || 0);
          const soldQty = Math.min(totalQty, Number(soldByPurchase.get(p.publicId) || 0));
          const remQty = Math.max(0, totalQty - soldQty);
          remainingQuantity += remQty;
          const perShareEUR = typeof p.pricePerShareEUR === 'number'
            ? Number(p.pricePerShareEUR)
            : Number(p.pricePerShare || 0) * (typeof p.cambio === 'number' ? Number(p.cambio) : (currency === 'EUR' ? 1 : stockCambio));
          remainingCostBasisEUR += remQty * perShareEUR;
        });

        // Dividends EUR
        const netDividends = Array.isArray(s.dividends)
          ? Number(s.dividends.reduce((sum: number, d: any) => sum + (Number(d.amount || 0)), 0).toFixed(2))
          : 0;

        // Current value in EUR
        const currentPriceCoin = Number((s as any).currentPrice || 0);
        const effectiveCambio = currency === 'EUR' ? 1 : (stockCambio && stockCambio > 0 ? stockCambio : 1);
        const currentValue = Number((remainingQuantity * currentPriceCoin * effectiveCambio).toFixed(2));

        const invested = Number(remainingCostBasisEUR.toFixed(2));
        const noDivReturn = Number((currentValue - invested).toFixed(2));
        const totalReturn = Number((noDivReturn + netDividends).toFixed(2));
        return { symbol: s.symbol, totalReturn, noDivReturn };
      });
      setReturnsData(returnsRows);
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Não foi possível carregar os dados do painel');
    } finally {
      setLoading(false);
    }
  };

  const calculatePortfolioSummary = (stocks: StockDto[]): PortfolioSummary => {
    let totalValue = 0;
    let totalCost = 0;
    let totalDividends = 0;
    let stocksCount = 0;
    let etfsCount = 0;

    stocks.forEach(stock => {
      // Calculate total shares and average price from purchases
      const totalShares = stock.purchases.reduce((sum, purchase) => sum + purchase.quantity, 0);
      const totalInvested = stock.purchases.reduce((sum, purchase) => 
        sum + (purchase.quantity * purchase.pricePerShare), 0);
      
      // Calculate current value if price is available
      const currentValue = totalShares * (stock.currentPrice || 0);
      
      totalCost += totalInvested;
      totalValue += currentValue;
      
      // Count stocks (all stocks for now, until we have investment type in DTO)
      stocksCount++;
      
      // Calculate dividends
      const stockDividends = stock.dividends.reduce((sum, dividend) => sum + dividend.amount, 0);
      totalDividends += stockDividends;
    });

    const totalGainLoss = totalValue - totalCost;
    const totalGainLossPercentage = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;
    const dividendYield = totalValue > 0 ? (totalDividends / totalValue) * 100 : 0;
    const monthlyDividends = totalDividends / 12;

    return {
      totalValue,
      totalCost,
      totalGainLoss,
      totalGainLossPercentage,
      stocksCount,
      etfsCount,
      dividendYield,
      monthlyDividends
    };
  };

  const calculateTopPerformers = (stocks: StockDto[]): TopPerformer[] => {
    return stocks
      .map(stock => {
        // Calculate total shares and invested amount from purchases
        const totalShares = stock.purchases.reduce((sum, purchase) => sum + purchase.quantity, 0);
        const totalInvested = stock.purchases.reduce((sum, purchase) => 
          sum + (purchase.quantity * purchase.pricePerShare), 0);
        
        // Calculate current value if price is available
        const currentValue = totalShares * (stock.currentPrice || 0);
        const gainLossPercentage = totalInvested > 0 ? ((currentValue - totalInvested) / totalInvested) * 100 : 0;
        
        return {
          symbol: stock.symbol,
          name: stock.symbol, // Using symbol as name since name is not available in DTO
          gainLossPercentage,
          currentValue
        };
      })
      .filter(stock => stock.currentValue > 0) // Only include stocks with current price
      .sort((a, b) => b.gainLossPercentage - a.gainLossPercentage)
      .slice(0, 5);
  };

  const formatCurrency = (amount: number): string => formatEuro(amount);
  const formatPercentage = (percentage: number): string => formatPercent(percentage);

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Painel da carteira de investimentos" />
      
      {/* Prominent Returns chart: With vs Without Dividends */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Rendimento: com e sem dividendos
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={returnsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="symbol" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatEuro(Number(value))} />
              <Legend />
              <Bar dataKey="totalReturn" name="Com dividendos" fill="#388e3c" />
              <Bar dataKey="noDivReturn" name="Sem dividendos" fill="#d32f2f" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      {/* Portfolio Overview Cards */}
      <AutoGrid min={150} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceWalletIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Valor total
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {formatCurrency(portfolioSummary?.totalValue || 0)}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {loading ? null : (portfolioSummary?.totalGainLoss || 0) >= 0 ? 
                <TrendingUpIcon color="success" sx={{ mr: 1 }} /> : 
                <TrendingDownIcon color="error" sx={{ mr: 1 }} />
              }
              <Typography variant="subtitle2" color="text.secondary">
                Lucro/Prejuízo total
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Box>
                <Typography 
                  variant="h6" 
                  component="div"
                  color={(portfolioSummary?.totalGainLoss || 0) >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(portfolioSummary?.totalGainLoss || 0)}
                </Typography>
                <Typography 
                  variant="caption" 
                  color={(portfolioSummary?.totalGainLoss || 0) >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatPercentage(portfolioSummary?.totalGainLossPercentage || 0)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ShowChartIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Ações individuais
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="30%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {portfolioSummary?.stocksCount || 0}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                ETFs
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="30%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {portfolioSummary?.etfsCount || 0}
              </Typography>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Dividend Information and Top Performers */}
      <AutoGrid min={360} gap={3} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informação de dividendos
            </Typography>
            {loading ? (
              <Box>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="60%" />
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rendimento da carteira:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumber(portfolioSummary?.dividendYield || 0)}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Dividendos mensais:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(portfolioSummary?.monthlyDividends || 0)}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Melhores desempenhos
            </Typography>
            {loading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="text" width="20%" />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box>
                {topPerformers.map((performer, index) => (
                  <Box 
                    key={performer.symbol} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: index < topPerformers.length - 1 ? 1 : 0,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: index === 0 ? 'action.hover' : 'transparent'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {performer.symbol}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {performer.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={formatPercentage(performer.gainLossPercentage)}
                      color={performer.gainLossPercentage >= 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                ))}
                {topPerformers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Sem dados de desempenho
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Quick Actions / Recent Activity */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Alocação da carteira
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PieChartIcon color="primary" />
          <Typography variant="body2" color="text.secondary">
            Veja a análise de alocação detalhada na secção Análise da carteira
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default StocksDashboard;
